import { AmplitudeExperiments, AmplitudeVariants } from '@/utils/amplitude/experiments/AmplitudeExperiments';
import { buildSearchQuery, isBuyNowQuery } from '@/utils/search/searchQuery';
import { CatalogAds } from '@/redux/modules/catalog/ad/catalogAd.types';
import { DataLoaderParams } from '@/types/DataLoaderParams';
import { fetchCatalogAdIfNeeded } from '@/redux/modules/catalog/ad/catalogAd.actions';
import { fetchCategoryRelatedSearches } from '@/redux/modules/categoryRelatedSearches';
import { fetchItemFacetsIfNeeded, getItemFacets } from '@/redux/modules/itemFacets';
import { fetchItemSummariesIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import { fetchRelatedSearchesForSearchPage } from '@/redux/modules/search/relatedSearches/relatedSearches.actions';
import { fetchSearchFacetsIfNeeded } from '@/redux/modules/searchFacets';
import { fetchSearchSuggestions, getSecondarySearchSuggestions } from '@/redux/modules/searchSuggestions';
import { fetchSellersIfNeeded } from '@/redux/modules/seller';
import { fetchSimilarItemsIfNeeded, getSimilarItemIds } from '@/redux/modules/similarItems';
import { FilterAction } from '@/types/SearchFacet';
import {
    getCategoryIdsFromQueryParams,
    getKeywordFromQueryParams,
    isArchivedSearch,
    parseQueryParams,
} from '@/utils/queryParams';
import { getFacetIds } from '@/utils/ItemPageUtils';
import { getIsCollectionsPage, getIsSEOSearchPage, getIsSEOSearchPageWithLocation } from '@/utils/urls';
import { getItemSummarySellerId, getItemSummaryTitle } from '@/redux/modules/item/summary/itemSummary.selectors';
import {
    getRawArchivedSearchFacets,
    getRawUpcomingSearchFacets,
} from '@/redux/modules/searchFacetAggregates.selectors';
import { getSearchedItemIds, getSearchUiState } from '@/redux/modules/search/search.selectors';
import { getSearchPageSize } from '@/redux/modules/pagination';
import { getSEOSearchPageBreadcrumbs } from '@/redux/modules/searchPromoted.selectors';
import { SearchPageName } from '@/types/search/searchQuery';
import { setIsZeroResults, submitSearch } from '@/redux/modules/search/search.actions';
import cleanSearchTerms from '@/utils/cleanSearchTerms';
import paramTypes from '@/types/search/enums/paramTypes';

export default ({ amplitudeExperimentFlags, location, routerState, store }: DataLoaderParams) => {
    const { dispatch, getState } = store;
    const queryParams = parseQueryParams(location.search);
    const { pathname } = location;

    const archivedSearch = isArchivedSearch(queryParams, pathname);

    const hasUTMSourceLASavedAlert = queryParams[paramTypes.UTM_SOURCE] === 'LA_SavedAlert';

    const hasFollowedSearch = queryParams[paramTypes.FOLLOWED_SEARCH] === 'true';

    // Experiment: SEAR-701 Auction House Search Filtering
    const auctionHouseSearchFilteringVariant =
        amplitudeExperimentFlags[AmplitudeExperiments.AuctionHouseSearchFiltering]?.value;
    const hasAuctionHouseSearchFiltering =
        auctionHouseSearchFilteringVariant === AmplitudeVariants.AuctionHouseSearchFiltering.Treatment;

    // Experiment: SEAR-646 Related Searches on SRPs
    const relatedSearchesOnSrpsVariant = amplitudeExperimentFlags[AmplitudeExperiments.RelatedSearchesOnSrps]?.value;
    const hasRelatedSearchesOnSrpsTreatment =
        relatedSearchesOnSrpsVariant === AmplitudeVariants.RelatedSearchesOnSrps.Treatment;

    // Experiment: SEAR-684 Predictive Average Lot Value in Elastic
    const predictiveAverageLotValueInElasticVariant =
        amplitudeExperimentFlags[AmplitudeExperiments.PredictiveAverageLotValueInElastic]?.value;
    const rankingVersionVariant = [
        AmplitudeVariants.PredictiveAverageLotValueInElastic.AddOnlyMLSignals,
        AmplitudeVariants.PredictiveAverageLotValueInElastic.FullRerank,
        AmplitudeVariants.PredictiveAverageLotValueInElastic.PredictedExpectedValueRerank,
    ].includes(predictiveAverageLotValueInElasticVariant)
        ? predictiveAverageLotValueInElasticVariant
        : undefined;

    // Experiment: SEAR-998 Popularity Score Re-Ranking
    const popularityScoreReRankingTreatment =
        amplitudeExperimentFlags[AmplitudeExperiments.PopularityScoreReRanking]?.value;

    let popularityScoreReRankingVersion: string | undefined;
    if (popularityScoreReRankingTreatment === AmplitudeVariants.PopularityScoreReRanking.AddPopularityKeepingBidCount) {
        popularityScoreReRankingVersion = 'v2';
    } else if (
        popularityScoreReRankingTreatment === AmplitudeVariants.PopularityScoreReRanking.AddPopularityRemovingBidCount
    ) {
        popularityScoreReRankingVersion = 'v3';
    }

    // For archive searches without category_id, skip initial facet fetch (facets loaded on demand when filters open).
    // When category_id is present, facets are needed for buildSearchQuery's category path mapping.
    // When a filter is being refined (filterAction present), always fetch facets.
    // Tab switches, sort changes, and pagination are NOT filter refinements — they don't affect facet counts.
    const nonFilterActions: FilterAction[] = [
        FilterAction.PageChanged,
        FilterAction.PageSizeChanged,
        FilterAction.SortByChanged,
        FilterAction.ViewPriceResults,
        FilterAction.ViewUpcomingItems,
    ];
    const isFilterRefinement =
        Boolean(routerState?.filterAction) && !nonFilterActions.includes(routerState?.filterAction);
    const shouldFetchFacetsOnLoad = !archivedSearch || Boolean(queryParams.category_id) || isFilterRefinement;

    const facetPromise = shouldFetchFacetsOnLoad
        ? dispatch(fetchSearchFacetsIfNeeded({ archivedSearch }))
        : Promise.resolve();

    return Promise.all([facetPromise]).then(async () => {
        const currentState = getState();

        const pageSize = getSearchPageSize(currentState);
        let rawFacets;
        if (archivedSearch) {
            rawFacets = getRawArchivedSearchFacets(currentState);
        } else {
            rawFacets = getRawUpcomingSearchFacets(currentState);
        }
        const searchQuery = buildSearchQuery({
            pathname,
            queryParams,
            rawFacets,
            searchPageSize: pageSize,
        });

        const isBIN = isBuyNowQuery(searchQuery);
        searchQuery.buyNow = !archivedSearch && isBIN;
        const { searchTerm } = searchQuery;

        let pageName = archivedSearch ? SearchPageName.SOLD_SEARCH : SearchPageName.SEARCH;

        const isSEOSearchPage = getIsSEOSearchPage(pathname) || getIsSEOSearchPageWithLocation(pathname);
        if (isSEOSearchPage) {
            pageName = SearchPageName.SEO_SEARCH;
        } else if (getIsCollectionsPage(pathname)) {
            pageName = SearchPageName.COLLECTIONS;
        }

        const keywords = getKeywordFromQueryParams(queryParams);

        const promises = [
            dispatch(
                submitSearch({
                    pageName,
                    popularityScoreReRankingVersion,
                    rankingVersionVariant,
                    routerState,
                    searchQuery,
                    skipAggs: !isFilterRefinement,
                    useATGSearch: !hasUTMSourceLASavedAlert && !hasFollowedSearch,
                    useAuctionHouseSearchFilteringExperiment: hasAuctionHouseSearchFiltering,
                    useZRPExperiment: true,
                })
            ),
            dispatch(fetchSearchSuggestions(cleanSearchTerms(searchTerm), true)),
            // fetch the search results page banner ad - with force in order to fetch a new ad each time the page changes
            dispatch(fetchCatalogAdIfNeeded({ force: true, pageName: CatalogAds.SearchPageBanner })),
        ];

        await Promise.all(promises);

        const searchState = getState();
        const itemIds = getSearchedItemIds(searchState);
        const { totalFound, totalSoldItems } = getSearchUiState(searchState);

        const shouldPreloadSoldItems = !archivedSearch && totalFound === 0 && totalSoldItems > 0;

        if (itemIds.length) {
            // If we have search results do all the stuff to get the data for them.
            const { houses } = getSecondarySearchSuggestions(searchState);

            const promises: Promise<any>[] = [
                dispatch(
                    fetchCatalogAdIfNeeded({
                        categoryIds: getCategoryIdsFromQueryParams(queryParams),
                        keywords: `${keywords}`,
                        pageName,
                    })
                ),
                // Fetch item summaries for search results to enable SSR
                dispatch(
                    fetchItemSummariesIfNeeded({
                        identifier: 'SEARCH-PAGE-RESULTS',
                        itemIds,
                    })
                ),
            ];

            if (Boolean(houses?.[0]?.houseId)) {
                promises.push(dispatch(fetchSellersIfNeeded([houses[0]?.houseId])));
            }

            if (isSEOSearchPage) {
                const searchPageBreadcrumbs = getSEOSearchPageBreadcrumbs(searchState);
                if (searchPageBreadcrumbs.length > 0) {
                    const relatedCategoryId = searchPageBreadcrumbs[searchPageBreadcrumbs.length - 1].id;
                    promises.push(dispatch(fetchCategoryRelatedSearches(itemIds, relatedCategoryId)));
                }
            }

            if (hasRelatedSearchesOnSrpsTreatment) {
                promises.push(dispatch(fetchRelatedSearchesForSearchPage(searchTerm)));
            }

            return Promise.all(promises);
        } else {
            // If we get no results back, we'll get the data for our zero results page
            if (shouldPreloadSoldItems) {
                await dispatch(setIsZeroResults(true));
                await dispatch(
                    submitSearch({
                        pageName,
                        routerState,
                        searchQuery: buildSearchQuery({
                            pathname,
                            queryParams: {
                                ...queryParams,
                                status: 'archive',
                            },
                            rawFacets,
                            searchPageSize: pageSize,
                            similarItems: true,
                        }),
                        skipAggs: true,
                        useATGSearch: true,
                        useZRPExperiment: false,
                    })
                );
            }

            const { soldItemIds = [] } = getSearchUiState(searchState);
            const promises = [
                dispatch(
                    fetchItemSummariesIfNeeded({
                        identifier: 'SEARCH-PAGE-SOLD',
                        itemIds: soldItemIds,
                        similarItems: true,
                    })
                ),
                dispatch(fetchItemFacetsIfNeeded([soldItemIds[0]])),
            ];

            return Promise.all(promises).then(() => {
                const state = getState();
                const searchState = getSearchUiState(state);

                if (searchState?.itemIds?.length > 0) {
                    const itemId = searchState.itemIds[0];
                    const sellerId = getItemSummarySellerId(state, itemId);
                    const title = getItemSummaryTitle(state, itemId);
                    const facets = getItemFacets(state, itemId);
                    const categoryIds = getFacetIds(facets);
                    return Promise.all([
                        dispatch(
                            fetchSimilarItemsIfNeeded({
                                categoryIds,
                                itemId,
                                sellerId,
                                title,
                            })
                        ),
                    ]).then(() => {
                        const state = getState();
                        const similarItemIds = getSimilarItemIds(state, itemId);
                        return dispatch(
                            fetchItemSummariesIfNeeded({
                                identifier: 'SEARCH-PAGE-SIMILAR',
                                itemIds: similarItemIds,
                            })
                        );
                    });
                }
            });
        }
    });
};
